import { useEffect, useState } from "react";
import "./IssueTabs.css";
import { Alert, Loading } from "./Card";
import { STATUS, StatusPill } from "./Status";
import { DepsEditor, ChildrenPanel } from "./IssueEdit";
import type { Issue, ProgressRow } from "../lib/tasks";
import { API, errText, tasksApi } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 이슈 상세의 탭 ══
   채택 시안 Figma 33:2(t-in-detail-tabs). b3x-pr 상세의 탭 구성(의도 · 완료 기준 · 정보 · 인계 ·
   이력)을 그대로 가져왔다 — 한 줄로 쭉 이어 두면 무엇을 보러 왔든 스크롤을 한참 내려야 했고,
   맨 아래 조작 줄이 묻혀 「취소 · 담당 해제가 안 된다」로 읽혔다.

   b3x-pr 의 「맥락」 탭은 없다 — b3x-in 이슈에는 그 칸이 없다(서버 · MCP 둘 다 안 받는다).
   조작 줄 · 열린 질문 · 막힘 보완은 **탭 밖**(위)이다 — 어느 탭을 보든 손이 닿아야 한다. */

export const ISSUE_TABS = ["intent", "acceptance", "info", "handoff", "history"] as const;
export type IssueTab = (typeof ISSUE_TABS)[number];

const TAB_LABEL: Record<IssueTab, () => string> = {
  intent: () => tx("issueTabs.intent"),
  acceptance: () => tx("issueTabs.acceptance"),
  info: () => tx("issueTabs.info"),
  handoff: () => tx("issueTabs.handoff"),
  history: () => tx("issueTabs.history"),
};

export function IssueTabs({
  t,
  all,
  onOpen,
  onChanged,
  epicTitle,
  milestoneLabel,
  initialTab = "intent",
  history,
}: {
  t: Issue;
  all: Issue[];
  onOpen: (id: string) => void;
  onChanged: () => void;
  epicTitle?: string;
  milestoneLabel?: string;
  initialTab?: IssueTab;
  /** 패턴 페이지가 요청 없이 이력 탭을 보이려고 쓴다. */
  history?: ProgressRow[];
}) {
  const [tab, setTab] = useState<IssueTab>(initialTab);
  // 다른 이슈를 열면 첫 탭부터 — 같은 이슈를 다시 그릴 때(저장 뒤)는 보던 탭을 지킨다.
  useEffect(() => setTab(initialTab), [t.id, initialTab]);
  return (
    <div className="itabs">
      <div className="b-tabs itabs__bar" role="tablist" aria-label={tx("issueTabs.label")}>
        {ISSUE_TABS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            id={`itab-${t.id}-${k}`}
            aria-selected={tab === k}
            aria-controls={`ipane-${t.id}-${k}`}
            className={`b-tab${tab === k ? " is-on" : ""}`}
            onClick={() => setTab(k)}
          >
            {TAB_LABEL[k]()}
          </button>
        ))}
      </div>
      <div className="itabs__pane" role="tabpanel" id={`ipane-${t.id}-${tab}`} aria-labelledby={`itab-${t.id}-${tab}`}>
        {tab === "intent" ? (
          t.intent ? <p className="itabs__text">{t.intent}</p> : <p className="muted">{tx("board.detail.notWritten")}</p>
        ) : tab === "acceptance" ? (
          t.acceptance.length ? (
            <ul className="dmodal__list">
              {t.acceptance.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">{tx("board.detail.acceptanceEmpty")}</p>
          )
        ) : tab === "info" ? (
          <InfoPane t={t} all={all} onOpen={onOpen} onChanged={onChanged} epicTitle={epicTitle} milestoneLabel={milestoneLabel} />
        ) : tab === "handoff" ? (
          t.handoff ? <p className="itabs__text">{t.handoff}</p> : <p className="muted">{tx("issueTabs.handoffNone")}</p>
        ) : (
          <HistoryPane id={t.id} rows={history} />
        )}
      </div>
    </div>
  );
}

function InfoPane({
  t,
  all,
  onOpen,
  onChanged,
  epicTitle,
  milestoneLabel,
}: {
  t: Issue;
  all: Issue[];
  onOpen: (id: string) => void;
  onChanged: () => void;
  epicTitle?: string;
  milestoneLabel?: string;
}) {
  // 후속(이 이슈를 선행으로 건 것)은 어디에도 저장돼 있지 않다 — 보드가 이미 읽은 목록을 훑는다.
  const nexts = all.filter((x) => x.dependsOn.includes(t.id));
  const atts = t.attachments ?? [];
  return (
    <>
      <DepsEditor key={`d-${t.id}`} t={t} all={all} onChanged={onChanged} />
      <section className="iedit__card">
        <h3>{tx("issueTabs.next", { n: nexts.length })}</h3>
        {nexts.length ? (
          <ul className="itabs__links">
            {nexts.map((x) => (
              <li key={x.id}>
                <button type="button" className="itabs__link" onClick={() => onOpen(x.id)}>
                  <span className="itabs__grow">{x.title}</span>
                  <StatusPill status={x.status} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{tx("issueTabs.nextNone")}</p>
        )}
      </section>
      <ChildrenPanel key={`c-${t.id}`} t={t} all={all} onChanged={onChanged} onOpen={onOpen} />
      <section className="iedit__card">
        <h3>{tx("issueTabs.attachments", { n: atts.length })}</h3>
        {atts.length ? (
          <ul className="itabs__links">
            {atts.map((a) => (
              <li key={a.id}>
                <a className="itabs__link" href={`${API}/attachments/${encodeURIComponent(a.id)}`} target="_blank" rel="noreferrer">
                  <span className="itabs__grow">{a.filename}</span>
                  <span className="muted tnum">{tx("issueTabs.attSize", { kb: Math.max(1, Math.round(a.size / 1024)), who: a.createdBy })}</span>
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{tx("issueTabs.attachmentsNone")}</p>
        )}
      </section>
      <dl className="kv">
        <dt>id</dt>
        <dd>{t.id}</dd>
        <dt>{tx("board.milestone")}</dt>
        <dd>{t.milestoneId ? (milestoneLabel ?? t.milestoneId) : tx("common.state.none")}</dd>
        <dt>{tx("board.detail.parent")}</dt>
        <dd>{epicTitle ?? tx("common.state.none")}</dd>
        <dt>{tx("board.detail.assignee")}</dt>
        {/* 담당이 없는 것과 「모른다」는 다르다. 빈칸을 그냥 두면 그 차이가 사라진다. */}
        <dd>
          {t.assignee
            ? `${t.assignee} · ${tx(t.assigneeKind === "held" ? "board.detail.held" : "board.detail.ticket")}`
            : tx("common.state.none")}
        </dd>
        <dt>PR</dt>
        <dd>
          {t.prLink ? (
            <a href={t.prLink.url} target="_blank" rel="noreferrer">
              {t.prLink.url.replace(/^https:\/\/github\.com\//, "")}
            </a>
          ) : (
            tx("common.state.none")
          )}
        </dd>
        <dt>{tx("issueTabs.createdAt")}</dt>
        {/* 못 읽은 값은 「모름」이다 — 빈칸으로 두지 않는다. */}
        <dd>{t.createdAt ? new Date(t.createdAt).toLocaleString() : tx("issueTabs.unknown")}</dd>
        <dt>{tx("issueTabs.updatedAt")}</dt>
        <dd>{new Date(t.updatedAt).toLocaleString()}</dd>
      </dl>
    </>
  );
}

function HistoryPane({ id, rows: given }: { id: string; rows?: ProgressRow[] }) {
  const [rows, setRows] = useState<ProgressRow[] | null>(given ?? null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (given) return;
    let live = true;
    setRows(null);
    setErr(null);
    tasksApi
      .progress(id)
      .then((r) => live && setRows(r))
      .catch((e) => live && setErr(errText(e)));
    return () => {
      live = false;
    };
  }, [id, given]);
  if (err) return <Alert tone="error">{tx("issueTabs.historyFail", { err })}</Alert>;
  if (!rows) return <Loading text={tx("issueTabs.historyLoading")} />;
  if (!rows.length) return <p className="muted">{tx("issueTabs.historyNone")}</p>;
  return (
    <ol className="itabs__hist">
      {rows.map((r, i) => (
        <li key={`${r.at}-${i}`}>
          <b>
            {r.from ? STATUS[r.from].label : "∅"} → {STATUS[r.to]?.label ?? r.to} · {r.actor}
          </b>
          <span className="muted">
            {new Date(r.at).toLocaleString()}
            {r.note ? ` · ${r.note}` : ""}
          </span>
        </li>
      ))}
    </ol>
  );
}
